import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Clock } from "lucide-react";
import { API, resolveImage } from "../products";
import { useLang } from "../i18n";
import { waLink, WhatsAppIcon } from "./WhatsApp";
import { track } from "../analytics";

const humanSize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "";
  }
};

// Self-contained download card used on the homepage and the /catalog page.
export const InventoryDownloadCard = () => {
  const { t, lang } = useLang();
  const d = t.catalog.dl;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/site/inventory-file`)
      .then((r) => r.json())
      .then((data) => setFile(data && data.exists ? data : null))
      .catch(() => setFile(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400" data-testid="inventory-download-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> …
      </div>
    );
  }

  if (!file) {
    return (
      <div className="max-w-xl mx-auto text-center rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="inventory-download-nofile">
        <p className="text-slate-600">{d.noFile}</p>
        <a
          href={waLink(lang === "es" ? "Hola, ¿me pueden enviar su lista de inventario?" : "Hi, can you send me your inventory list?")}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track("whatsapp_click", { label: "inventory_nofile" })}
          className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-bold transition-colors"
        >
          <WhatsAppIcon className="w-5 h-5" /> {d.contactWhatsApp}
        </a>
      </div>
    );
  }

  return (
    <div
      className="max-w-xl mx-auto rounded-3xl border border-slate-200 bg-white p-8 shadow-lg text-center"
      data-testid="inventory-download-card"
    >
      <div className="mx-auto w-16 h-16 rounded-2xl bg-green-600/10 flex items-center justify-center">
        <FileSpreadsheet className="w-8 h-8 text-green-600" />
      </div>
      <p className="mt-4 font-bold text-slate-800 break-all" data-testid="inventory-file-name">{file.filename}</p>
      <div className="mt-1 flex items-center justify-center gap-3 text-xs text-slate-400">
        {file.size ? <span>{humanSize(file.size)}</span> : null}
        {file.updated_at ? (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {d.updated} {fmtDate(file.updated_at, lang)}
          </span>
        ) : null}
      </div>
      <a
        href={resolveImage(file.url)}
        onClick={() => track("inventory_download", { label: file.filename })}
        data-testid="inventory-download-btn"
        className="mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-600 hover:bg-green-700 text-white font-extrabold text-lg shadow-lg shadow-green-600/20 transition-all hover:-translate-y-0.5"
      >
        <Download className="w-5 h-5" /> {d.button}
      </a>
    </div>
  );
};

// Homepage section wrapper.
export const InventoryDownloadSection = () => {
  const { t } = useLang();
  const d = t.catalog.dl;
  return (
    <section id="inventory-download" className="max-w-[1280px] mx-auto px-6 py-16">
      <div className="text-center mb-10">
        <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">{d.homeTitle}</h2>
        <p className="mt-3 text-slate-500 max-w-2xl mx-auto">{d.homeSubtitle}</p>
      </div>
      <InventoryDownloadCard />
    </section>
  );
};
